import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Package, Copy } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { ApiError, errorDetail } from "@/lib/api-client";
import {
  Alert, Badge, Button, PageHeader, Pagination, Spinner, Switch,
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow, toast,
} from "@/components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CloneGlobalDialog } from "./CloneGlobalDialog";
import { useProducts, useUpdateProduct, useDeleteProduct } from "./hooks";
import type { CloneGlobalResult, Product } from "./api";

const MEASURE_TEXT: Record<string, string> = {
  area_tiered: "按面积",
  thickness_size_tiered: "按尺寸",
};

function measureText(p: Product): string {
  if (MEASURE_TEXT[p.calc_strategy]) return MEASURE_TEXT[p.calc_strategy];
  try {
    const schema = JSON.parse(p.schema_json || "{}") as { measure?: string };
    return schema.measure === "length" ? "按尺寸" : "按面积";
  } catch {
    return "按面积";
  }
}

export function ProductsPage() {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const isSuper = me?.role === "super_admin";
  // 从全局默认克隆只对公司管理员有意义（超管没有 company_id，后端会返回 400）
  const canClone = me?.role === "company_admin";
  const [showClone, setShowClone] = useState(false);
  const [cloneResult, setCloneResult] = useState<CloneGlobalResult | null>(null);
  const [scope, setScope] = useState<string | undefined>(undefined);

  const { data, isLoading, error } = useProducts(scope);
  const updateMut = useUpdateProduct(scope);
  const deleteMut = useDeleteProduct(scope);

  const [confirmDel, setConfirmDel] = useState<Product | null>(null);
  const [confirmOff, setConfirmOff] = useState<Product | null>(null);
  const [page, setPage] = useState(1);

  const scopeQuery = scope ? `?scope=${scope}` : "";
  const products = data?.data ?? [];
  const PAGE_SIZE = 10;
  // 删除/切 scope 后条数变少时，把页码拉回有效范围
  const safePage = Math.min(page, Math.max(1, Math.ceil(products.length / PAGE_SIZE)));
  const pageItems = products.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const setActive = (p: Product, on: boolean) =>
    updateMut.mutate(
      { id: p.id, payload: { is_active: on ? 1 : 0 } },
      {
        onSuccess: () => toast.success(on ? `已启用「${p.name}」` : `已停用「${p.name}」`),
        onError: (e) => toast.error(on ? "启用失败" : "停用失败", { description: errorDetail(e, "请稍后重试") }),
      },
    );

  const addProduct = () => navigate(`/products/new${scopeQuery}`);

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="产品"
        description="管理可报价的产品、价格档位与附加费用。修改保存后立即对新询价生效。"
        actions={
          <>
            {isSuper && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={scope === "global"}
                  onChange={(e) => { setScope(e.target.checked ? "global" : undefined); setPage(1); }}
                />
                全局默认产品
              </label>
            )}
            {canClone && (
              <Button variant="outline" icon={<Copy />} onClick={() => { setCloneResult(null); setShowClone(true); }}>
                从全局默认克隆
              </Button>
            )}
            <Button icon={<Plus />} onClick={addProduct}>添加产品</Button>
          </>
        }
      />

      {error && <Alert>{error instanceof ApiError ? `加载失败 (${error.status})` : "加载失败"}</Alert>}

      {cloneResult && (
        <Alert variant="success" onClose={() => setCloneResult(null)}>
          克隆完成：产品 {cloneResult.products} 个，提示词 {cloneResult.prompts} 个，LLM 配置 {cloneResult.llm_configs} 个。
        </Alert>
      )}

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-4 sm:pl-5">产品名称</TableHead>
            <TableHead className="hidden md:table-cell">Key</TableHead>
            <TableHead className="hidden w-28 sm:table-cell">计价方式</TableHead>
            <TableHead numeric className="hidden w-20 lg:table-cell">规则数</TableHead>
            <TableHead numeric className="hidden w-20 lg:table-cell">修饰项</TableHead>
            <TableHead numeric className="hidden w-16 lg:table-cell">排序</TableHead>
            <TableHead className="w-20">状态</TableHead>
            <TableHead className="w-24 pr-4 text-right sm:pr-5">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <tr>
              <td colSpan={8} className="py-14 text-center text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2"><Spinner /> 加载中…</span>
              </td>
            </tr>
          )}
          {!isLoading && products.length === 0 && (
            <TableEmpty
              colSpan={8}
              icon={Package}
              title="还没有产品"
              hint="先添加一个来开始报价。"
              action={<Button size="sm" icon={<Plus />} onClick={addProduct}>添加产品</Button>}
            />
          )}
          {pageItems.map((p) => (
            <TableRow
              key={p.id}
              onClick={() => navigate(`/products/${p.id}${scopeQuery}`)}
              className="cursor-pointer"
            >
              <TableCell className="pl-4 sm:pl-5">
                <div className="font-medium text-foreground">{p.name}</div>
                <div className="mt-0.5 whitespace-nowrap text-xs text-muted-foreground md:hidden">
                  <span className="font-mono">{p.key}</span>
                  <span className="sm:hidden"> · {measureText(p)}</span>
                </div>
              </TableCell>
              <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">{p.key}</TableCell>
              <TableCell className="hidden sm:table-cell"><Badge>{measureText(p)}</Badge></TableCell>
              <TableCell numeric className="hidden text-muted-foreground lg:table-cell">{p.rule_count ?? "-"}</TableCell>
              <TableCell numeric className="hidden text-muted-foreground lg:table-cell">{p.modifier_count ?? "-"}</TableCell>
              <TableCell numeric className="hidden text-muted-foreground lg:table-cell">{p.sort_order}</TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={!!p.is_active}
                  aria-label={p.is_active ? "停用" : "启用"}
                  disabled={updateMut.isPending}
                  onChange={(on) => (on ? setActive(p, true) : setConfirmOff(p))}
                />
              </TableCell>
              <TableCell className="pr-4 text-right sm:pr-5" onClick={(e) => e.stopPropagation()}>
                <div className="inline-flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => navigate(`/products/${p.id}${scopeQuery}`)}
                    aria-label="编辑"
                    title="编辑"
                    className="text-muted-foreground hover:text-primary"
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setConfirmDel(p)}
                    aria-label="删除"
                    title="删除"
                    className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Pagination total={products.length} page={safePage} pageSize={PAGE_SIZE} onChange={setPage} />

      <CloneGlobalDialog
        open={showClone}
        onClose={() => setShowClone(false)}
        onDone={(r) => { setShowClone(false); setCloneResult(r); }}
      />

      <ConfirmDialog
        open={!!confirmOff}
        title={`停用产品「${confirmOff?.name ?? ""}」`}
        message="停用后该产品不再参与报价计算，可随时重新启用。"
        destructive
        confirmText="停用"
        onCancel={() => setConfirmOff(null)}
        onConfirm={() => {
          if (confirmOff) setActive(confirmOff, false);
          setConfirmOff(null);
        }}
      />

      <ConfirmDialog
        open={!!confirmDel}
        title={`删除产品「${confirmDel?.name ?? ""}」`}
        message="删除后产品及其价格规则将被永久移除，不可恢复。如只是暂时下架，请用列表中的开关停用。"
        destructive
        confirmText="永久删除"
        onCancel={() => setConfirmDel(null)}
        onConfirm={() => {
          if (confirmDel) {
            const name = confirmDel.name;
            deleteMut.mutate(confirmDel.id, {
              onSuccess: () => toast.success(`已删除「${name}」`),
              onError: (e) => toast.error("删除失败", { description: errorDetail(e, "请稍后重试") }),
            });
          }
          setConfirmDel(null);
        }}
      />
    </div>
  );
}
